import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material'
import {
  Home as HomeIcon,
  School as SchoolIcon,
  Games as GamesIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Class as ClassIcon,
  Assignment as AssignmentIcon,
  Translate as TranslateIcon,
  Save as SaveIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
  Group as GroupIcon,
  Quiz as QuizIcon,
  Settings as SettingsIcon
} from '@mui/icons-material'
import { signOut } from 'firebase/auth'
import { auth } from '../firebase/config'
import { useAuth } from '../hooks/useAuth'
import toast from 'react-hot-toast'
import StudentClasses from './StudentClasses'
import Settings from './Settings'
import TeacherDashboard from './TeacherDashboard'
import Translate from './Translate'
import Memory from './Memory'
import Schedule from './Schedule'
import GhostTutorial from './GhostTutorial'
import ClassDetail from './ClassDetail'
import StudentTeachers from './StudentTeachers'
import SendQuestion from './SendQuestion'
import StudentAnswers from './StudentAnswers'
import AnswerDetail from './AnswerDetail'
import StudentDocuments from './StudentDocuments'
import StudentTasks from './StudentTasks'
import LevelChoice from './LevelChoice'
import Education from './Education'
import Reading from './Reading'
import BookDetail from './BookDetail'
import Listening from './Listening'
import WordList from './WordList'
import Leaderboard from './Leaderboard'
import GameSelection from './GameSelection'
import WordMatch from './WordMatch'
import WordMatchLevelSelection from './WordMatchLevelSelection'
import WordScramble from './WordScramble'
import WordScrambleLevelSelection from './WordScrambleLevelSelection'
import QuizLevelSelection from './QuizLevelSelection'
import Quiz from './Quiz'
import ListeningWriteLevelSelection from './ListeningWriteLevelSelection'
import ListeningWrite from './ListeningWrite'
import SavedWordsGames from './SavedWordsGames'
import SavedWordsMatch from './SavedWordsMatch'
import SavedWordsQuiz from './SavedWordsQuiz'
import SpaceTheme from '../components/SpaceTheme'
import AdPlacement from '../components/AdPlacement'
import { useUrlNavigation } from '../hooks/useUrlNavigation'

const Dashboard = ({ userData }) => {
  const { user } = useAuth()
  const [anchorEl, setAnchorEl] = useState(null)
  const { currentPage, navigateToPage, goBack } = useUrlNavigation('dashboard')
  const setCurrentPage = navigateToPage // Eski API uyumluluğu için
  console.log('Dashboard initial currentPage:', currentPage)

  // İlk yüklemede hash yoksa dashboard hash'i ekle
  useEffect(() => {
    if (!window.location.hash) {
      window.location.hash = 'dashboard'
    }
  }, [])

  const [classDetailData, setClassDetailData] = useState(null)
  const [teacherData, setTeacherData] = useState(null)
  const [questionData, setQuestionData] = useState(null)
  const [bookData, setBookData] = useState(null)
  const [selectedLevel, setSelectedLevel] = useState(null)
  const [gameUserLevel, setGameUserLevel] = useState(null)
  const [pageHistory, setPageHistory] = useState(['dashboard'])

  const handleLogout = async () => {
    try {
      // Çıkış yapmadan önce hash'i temizle
      window.location.hash = ''
      // URL'yi temizle
      window.history.replaceState(null, '', window.location.pathname)
      
      await signOut(auth)
      toast.success('Başarıyla çıkış yaptınız')
    } catch (error) {
      console.error('Logout error:', error)
      toast.error('Çıkış yapılırken hata oluştu')
    }
  }

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  // Avatar için kullanıcı bilgilerini hazırla
  const getAvatarProps = () => {
    if (userData?.profileImageUrl) {
      return { src: userData.profileImageUrl, alt: userData.username }
    }
    
    const displayName = userData?.username || user?.displayName || user?.email?.split('@')[0] || 'U'
    return { 
      children: displayName.charAt(0).toUpperCase(),
      alt: displayName
    }
  }

  const isTeacher = userData?.role === 'teacher'

    // Öğrenci menü kartları - Android uygulamasındaki sırayla
  const studentMenuItems = [
    {
      id: 'classes',
      title: 'Sınıflarım',
      icon: <ClassIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
      description: 'Katıldığım sınıfları görüntüle',
      color: 'primary',
      action: () => 'student-classes'
    },
    {
      id: 'teachers',
      title: 'Öğretmenlerim',
      icon: <GroupIcon sx={{ fontSize: 32, color: 'secondary.main' }} />,
      description: 'Öğretmenlerimi görüntüle',
      color: 'secondary',
      action: () => 'student-teachers'
    },
    {
      id: 'tasks',
      title: 'Görevler',
      icon: <QuizIcon sx={{ fontSize: 32, color: 'warning.main' }} />,
      description: 'Atanan görevleri tamamla',
      color: 'warning',
      action: () => 'student-tasks'
    },
    {
      id: 'schedule',
      title: 'Ders Programı',
      icon: <ScheduleIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
      description: 'Ders programını görüntüle',
      color: 'primary',
      action: () => 'schedule'
    },
          {
        id: 'answers',
        title: 'Gelen Yanıtlar',
        icon: <CheckCircleIcon sx={{ fontSize: 32, color: 'success.main' }} />,
        description: 'Öğretmen yanıtlarını görüntüle',
        color: 'success',
        action: () => 'student-answers'
      },
          {
        id: 'documents',
        title: 'Gelen Dökümanlar',
        icon: <DescriptionIcon sx={{ fontSize: 32, color: 'info.main' }} />,
        description: 'Paylaşılan dökümanları incele',
        color: 'info',
        action: () => 'student-documents'
      },
    {
      id: 'translate',
      title: 'Çeviri',
      icon: <TranslateIcon sx={{ fontSize: 32, color: 'secondary.main' }} />,
      description: 'Hızlı çeviri yap',
      color: 'secondary',
      action: () => 'translate'
    },
    {
      id: 'memory',
      title: 'Hafıza',
      icon: <SaveIcon sx={{ fontSize: 32, color: 'success.main' }} />,
      description: 'Kaydedilen kelimeleri tekrarla',
      color: 'success',
      action: () => 'memory'
    }
  ]

  // Öğretmen menü kartları
  const teacherMenuItems = [
    {
      id: 'classes',
      title: 'Sınıflarım',
      icon: <ClassIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
      description: 'Sınıflarımı yönet',
      color: 'primary',
      action: () => 'teacher-dashboard'
    },
    {
      id: 'assignments',
      title: 'Ödevler',
      icon: <AssignmentIcon sx={{ fontSize: 32, color: 'secondary.main' }} />,
      description: 'Ödev oluştur ve yönet',
      color: 'secondary',
      action: () => 'student-tasks'
    }
  ]

  const handleMenuItemClick = (item) => {
    console.log('🔵 Menu item clicked:', item.title, 'current:', currentPage)
    if (item.action) {
      // Önce yeni sayfayı geçmişe ekle, sonra sayfayı değiştir
      const newPage = item.action()
      console.log('🔵 Action returned page:', newPage)
      if (newPage) {
        console.log('🔵 Adding to history:', currentPage, '-> going to:', newPage)
        setPageHistory(prev => {
          const newHistory = [...prev, currentPage]
          console.log('🔵 New history:', newHistory)
          return newHistory
        })
        setCurrentPage(newPage)
      }
    }
  }

  const handleBackToDashboard = (data) => {
    console.log('handleBackToDashboard called with:', data)
    console.log('Current page before:', currentPage)
    console.log('Data type:', typeof data)
    console.log('Data page:', data?.page)
    
    if (data && data.page) {
      // Önce sayfa verilerini ayarla
      if (data.page === 'class-detail') {
        setClassDetailData(data.classData)
      } else if (data.page === 'send-question') {
        setTeacherData(data.teacherData)
      } else if (data.page === 'answer-detail') {
        setQuestionData(data.questionData)
      } else if (data.page === 'book-detail') {
        setBookData(data.bookData)
      } else if (data.page === 'word-match-level-selection' || 
                 data.page === 'word-scramble-level-selection' || 
                 data.page === 'quiz-level-selection' || 
                 data.page === 'listening-write-level-selection' ||
                 data.page === 'saved-words-games' ||
                 data.page === 'saved-words-match' ||
                 data.page === 'saved-words-quiz') {
        if (data.userLevel) {
          setGameUserLevel(data.userLevel)
        }
      } else if (data.page === 'word-match' || 
                 data.page === 'word-scramble' || 
                 data.page === 'quiz' || 
                 data.page === 'listening-write') {
        if (data.selectedLevel) {
          setSelectedLevel(data.selectedLevel)
        } else {
          toast.error('Level seçilmedi')
        }
      } else if (data.page === 'education') {
        // Education sayfasına yönlendirme
        console.log('Navigating to education page')
      } else if (data.page === 'teacher-dashboard') {
        // Öğretmen dashboard'ına yönlendirme
        console.log('Navigating to teacher dashboard')
      } else if (data.page === 'leaderboard') {
        // Leaderboard'a yönlendirme
        console.log('Navigating to leaderboard')
      } else if (data.page === 'dashboard') {
        // Dashboard'a yönlendirme
        console.log('Navigating to dashboard')
      } else if (data.page === 'settings') {
        // Settings'e yönlendirme
        console.log('Navigating to settings')
      }
      
      // Son olarak sayfayı değiştir - data'lar hazırlandıktan sonra
      setPageHistory(prev => [...prev, currentPage])
      setCurrentPage(data.page)
    } else {
      // Geri git - bir önceki sayfaya dön
      console.log('🔴 Back without data, current history:', pageHistory)
      const newHistory = [...pageHistory]
      const previousPage = newHistory[newHistory.length - 1] || 'dashboard'
      console.log('🔴 Previous page from history:', previousPage)
      
      // Mevcut sayfayı geçmişten çıkar
      newHistory.pop()
      console.log('🔴 New history after pop:', newHistory)
      setPageHistory(newHistory)
      setCurrentPage(previousPage)
      
      // Eğer dashboard'a dönüyorsa state'leri temizle
      if (previousPage === 'dashboard') {
        console.log('🔴 Clearing states because returning to dashboard')
        setClassDetailData(null)
        setTeacherData(null)
        setQuestionData(null)
        setBookData(null)
        setSelectedLevel(null)
        setGameUserLevel(null)
      }
    }
  }



  // Sınıflarım sayfasını göster
  if (currentPage === 'student-classes') {
    return <StudentClasses userData={userData} onBack={handleBackToDashboard} />
  }

  // Sınıf Detay sayfasını göster
  if (currentPage === 'class-detail' && classDetailData) {
    return <ClassDetail 
      classData={classDetailData} 
      onBack={handleBackToDashboard}
      userData={userData}
    />
  }

  // Öğretmenlerim sayfasını göster
  if (currentPage === 'student-teachers') {
    return <StudentTeachers onBack={handleBackToDashboard} />
  }

  // Görevlerim sayfasını göster
  if (currentPage === 'student-tasks') {
    return <StudentTasks userData={userData} onBack={handleBackToDashboard} />
  }

  // Soru gönderme sayfasını göster
  if (currentPage === 'send-question' && teacherData) {
    return <SendQuestion teacherData={teacherData} onBack={handleBackToDashboard} />
  }

  // Gelen yanıtlar sayfasını göster
  if (currentPage === 'student-answers') {
    return <StudentAnswers onBack={handleBackToDashboard} />
  }

  // Yanıt detay sayfasını göster
  if (currentPage === 'answer-detail' && questionData) {
    return <AnswerDetail questionData={questionData} onBack={handleBackToDashboard} />
  }

  // Gelen dökümanlar sayfasını göster
  if (currentPage === 'student-documents') {
    return <StudentDocuments onBack={handleBackToDashboard} />
  }

  // Seviye seçimi sayfasını göster
  if (currentPage === 'level-choice') {
    return <LevelChoice userData={userData} onBack={handleBackToDashboard} />
  }

  // Eğitim sayfasını göster
  if (currentPage === 'education') {
    return <Education userData={userData} onBack={handleBackToDashboard} />
  }

  // Okuma sayfasını göster
  if (currentPage === 'reading') {
    return <Reading userData={userData} onBack={handleBackToDashboard} />
  }

  // Kitap detay sayfasını göster
  if (currentPage === 'book-detail' && bookData) {
    return <BookDetail bookData={bookData} onBack={handleBackToDashboard} />
  }



  // Settings sayfasını göster
  if (currentPage === 'settings') {
    return <Settings userData={userData} onBack={handleBackToDashboard} />
  }

  // Öğretmen dashboard'ını göster
  if (currentPage === 'teacher-dashboard') {
    console.log('Rendering TeacherDashboard with onBack:', handleBackToDashboard)
    console.log('handleBackToDashboard type:', typeof handleBackToDashboard)
    console.log('handleBackToDashboard toString:', handleBackToDashboard?.toString())
    return <TeacherDashboard userData={userData} onBack={handleBackToDashboard} />
  }

  // Çeviri sayfasını göster
  if (currentPage === 'translate') {
    return <Translate userData={userData} onBack={handleBackToDashboard} />
  }

  // Hafıza sayfasını göster
  if (currentPage === 'memory') {
    return <Memory 
      userData={userData} 
      onBack={handleBackToDashboard}
      onNavigateToTranslate={() => setCurrentPage('translate')}
    />
  }

  // Ders Programı sayfasını göster
  if (currentPage === 'schedule') {
    return <Schedule 
      userData={userData} 
      onBack={handleBackToDashboard}
    />
  }

  // Ghost Tutorial sayfasını göster
  if (currentPage === 'ghost-tutorial') {
    return <GhostTutorial 
      userData={userData} 
      onBack={handleBackToDashboard}
    />
  }

  // Dinleme sayfasını göster
  if (currentPage === 'listening') {
    return <Listening 
      userData={userData} 
      onBack={handleBackToDashboard}
    />
  }

  // Kelime listesi sayfasını göster
  if (currentPage === 'word-list') {
    return <WordList 
      userData={userData} 
      onBack={handleBackToDashboard}
    />
  }

  // Leaderboard sayfasını göster
  if (currentPage === 'leaderboard') {
    return <Leaderboard 
      userData={userData} 
      onBack={handleBackToDashboard}
    />
  }

  // Word Match level seçim sayfasını göster
  console.log('Dashboard render - currentPage:', currentPage)
  if (currentPage === 'word-match-level-selection') {
    console.log('Rendering WordMatchLevelSelection')
    return <WordMatchLevelSelection 
      userData={userData} 
      userLevel={gameUserLevel || userData?.level}
      onBack={handleBackToDashboard}
    />
  }

  // Word Match oyununu göster
  if (currentPage === 'word-match') {
    return <WordMatch 
      userData={userData} 
      onBack={handleBackToDashboard}
      selectedLevel={selectedLevel}
    />
  }

  // Word Scramble level seçim sayfasını göster
  if (currentPage === 'word-scramble-level-selection') {
    console.log('Rendering WordScrambleLevelSelection')
    return <WordScrambleLevelSelection 
      userData={userData} 
      userLevel={gameUserLevel || userData?.level}
      onBack={handleBackToDashboard}
    />
  }

  // Word Scramble oyununu göster
  if (currentPage === 'word-scramble') {
    return <WordScramble 
      userData={userData} 
      onBack={handleBackToDashboard}
      selectedLevel={selectedLevel}
    />
  }

  // Quiz level seçim sayfasını göster
  if (currentPage === 'quiz-level-selection') {
    console.log('Rendering QuizLevelSelection')
    return <QuizLevelSelection 
      userData={userData} 
      userLevel={gameUserLevel || userData?.level}
      onBack={handleBackToDashboard}
    />
  }

  // Quiz oyununu göster
  if (currentPage === 'quiz') {
    return <Quiz 
      userData={userData} 
      onBack={handleBackToDashboard}
      selectedLevel={selectedLevel}
    />
  }

  // ListeningWrite level seçim sayfasını göster
  if (currentPage === 'listening-write-level-selection') {
    console.log('Rendering ListeningWriteLevelSelection')
    return <ListeningWriteLevelSelection 
      userData={userData} 
      userLevel={gameUserLevel || userData?.level}
      onBack={handleBackToDashboard}
    />
  }

  // ListeningWrite oyununu göster
  if (currentPage === 'listening-write') {
    return <ListeningWrite 
      userData={userData} 
      onBack={handleBackToDashboard}
      selectedLevel={selectedLevel}
    />
  }

  // Saved Words Games sayfasını göster
  if (currentPage === 'saved-words-games') {
    return <SavedWordsGames 
      userData={userData} 
      onBack={handleBackToDashboard}
    />
  }

  // Saved Words Match oyununu göster
  if (currentPage === 'saved-words-match') {
    console.log('Rendering SavedWordsMatch with:', {
      gameUserLevel,
      userDataLevel: userData?.level,
      finalUserLevel: gameUserLevel || userData?.level
    })
    return <SavedWordsMatch 
      userData={userData} 
      onBack={handleBackToDashboard}
      userLevel={gameUserLevel || userData?.level}
    />
  }

  // Saved Words Quiz oyununu göster
  if (currentPage === 'saved-words-quiz') {
    console.log('Rendering SavedWordsQuiz with:', {
      gameUserLevel,
      userDataLevel: userData?.level,
      finalUserLevel: gameUserLevel || userData?.level
    })
    return <SavedWordsQuiz 
      userData={userData} 
      onBack={handleBackToDashboard}
      userLevel={gameUserLevel || userData?.level}
    />
  }

  // Oyun seçim sayfasını göster
  if (currentPage === 'game-selection') {
    return <GameSelection 
      userData={userData} 
      userLevel={gameUserLevel || userData?.level}
      onBack={handleBackToDashboard}
    />
  }

  return (
    <SpaceTheme>
      {/* App Bar */}
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          {/* Logo */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: 'white', 
            borderRadius: '50%', 
            width: { xs: 40, sm: 56, md: 66 },
            height: { xs: 40, sm: 56, md: 66 },
            marginRight: { xs: 1, sm: 2 },
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <img 
              src="/logo.png" 
              alt="EngliX Logo" 
              style={{ 
                width: window.innerWidth < 600 ? 28 : window.innerWidth < 900 ? 40 : 50, 
                height: window.innerWidth < 600 ? 28 : window.innerWidth < 900 ? 40 : 50, 
                objectFit: 'contain'
              }}
              onError={(e) => {
                console.log('Logo yüklenemedi, alternatif yollar deneniyor...')
                // Alternatif logo yollarını dene
                if (e.target.src.includes('logo.png')) {
                  e.target.src = '/logo.jpg'
                } else if (e.target.src.includes('logo.jpg')) {
                  e.target.src = '/logo.svg'
                } else if (e.target.src.includes('logo.svg')) {
                  e.target.src = '/logo.webp'
                } else {
                  e.target.style.display = 'none'
                  console.log('Logo dosyası bulunamadı')
                }
              }}
            />
          </Box>
          
          <Typography variant="h5" component="div" sx={{ 
            flexGrow: 1,
            fontWeight: 'bold',
            letterSpacing: '0.5px',
            fontSize: { xs: '1rem', sm: '1.5rem' }
          }}>
            EngliX
          </Typography>
          
          {/* Rol Chip'i - Kompakt */}
          <Chip 
            label={isTeacher ? 'Öğretmen' : 'Öğrenci'} 
            color={isTeacher ? 'secondary' : 'primary'}
            size="small"
            sx={{ 
              mr: 1,
              fontSize: { xs: '0.7rem', sm: '0.8rem' },
              height: { xs: 24, sm: 32 }
            }}
          />

          {/* Navigation Buttons */}
          {/* Education Button - Her zaman görünür */}
          <IconButton
            color="inherit"
            onClick={() => {
              setPageHistory(prev => [...prev, currentPage])
              setCurrentPage('education')
            }}
            sx={{ mr: 1 }}
          >
            <SchoolIcon />
          </IconButton>
          
          {/* Settings Button - Sadece masaüstü */}
          <IconButton
            color="inherit"
            onClick={() => {
              setPageHistory(prev => [...prev, currentPage])
              setCurrentPage('settings')
            }}
            sx={{ 
              mr: { xs: 1, sm: 2 },
              display: { xs: 'none', md: 'flex' }
            }}
          >
            <SettingsIcon />
          </IconButton>
          
          <IconButton
            size="large"
            edge="end"
            color="inherit"
            onClick={handleMenuOpen}
            sx={{
              p: { xs: 0.5, sm: 1 }
            }}
          >
            <Avatar 
              sx={{ 
                width: { xs: 36, sm: 32 }, 
                height: { xs: 36, sm: 32 },
                border: '2px solid rgba(255,255,255,0.5)'
              }}
              {...getAvatarProps()}
            />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleMenuClose}>
              <PersonIcon sx={{ mr: 1 }} />
              Profil
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ mr: 1 }} />
              Çıkış Yap
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="sm" sx={{ 
        py: { xs: 1, sm: 2, md: 3 },
        px: { xs: 1, sm: 2 }
      }}>
        {/* Header Card - Android uygulamasındaki gibi */}
        <Card sx={{ 
          mx: { xs: 0.5, sm: 2 }, 
          mt: { xs: 1, sm: 2 }, 
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center">
                <Avatar
                  sx={{ 
                    width: 56, 
                    height: 56, 
                    mr: 2,
                    cursor: 'pointer',
                    border: '2px solid #e0e0e0'
                  }}
                  {...getAvatarProps()}
                />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
                    {userData?.username || user?.displayName || (isTeacher ? 'Öğretmen' : 'Öğrenci')}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {isTeacher ? 'Öğretmen' : 'Öğrenci'}
                  </Typography>
                </Box>
              </Box>
              
                             {/* Ghost Help Button */}
               <IconButton
                 onClick={() => {
                   setPageHistory(prev => [...prev, currentPage])
                   setCurrentPage('ghost-tutorial')
                 }}
                 sx={{
                   width: 40,
                   height: 40,
                   backgroundColor: '#1976d2',
                   color: 'white',
                   '&:hover': {
                     backgroundColor: '#1565c0'
                   }
                 }}
               >
                 <Typography variant="h6">👻</Typography>
               </IconButton>
            </Box>
          </CardContent>
        </Card>

        {/* Native Reklam */}
        <Box sx={{ mx: 2, mt: 1.5 }}>
          <AdPlacement 
            type="native" 
            position="content" 
            userRole={userData?.role}
            pageType="general"
          />
        </Box>

        {/* Menu Items - Android uygulamasındaki sırayla */}
        <Box sx={{ px: 0 }}>
          <Typography variant="h6" sx={{ 
            px: 2, 
            mb: 2, 
            color: '#6E6D6D',
            fontWeight: 'bold',
            fontSize: '20px'
          }}>
            {isTeacher ? 'Öğretmen İşlemleri' : 'Öğrenci İşlemleri'}
          </Typography>
          
          <List sx={{ px: 0 }}>
            {(isTeacher ? teacherMenuItems : studentMenuItems).map((item, index) => (
              <React.Fragment key={item.id}>
                <ListItem
                  button
                  onClick={() => handleMenuItemClick(item)}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 1)',
                      transform: 'translateX(4px) scale(1.02)',
                      transition: 'all 0.3s ease-in-out',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.3)'
                    }
                  }}
                >
                  <ListItemIcon>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{
                      variant: 'h6',
                      fontWeight: 'bold',
                      color: '#333'
                    }}
                  />
                </ListItem>
                {index < (isTeacher ? teacherMenuItems : studentMenuItems).length - 1 && (
                  <Divider sx={{ my: 1, backgroundColor: 'rgba(255, 255, 255, 0.2)' }} />
                )}
              </React.Fragment>
            ))}
          </List>
        </Box>


      </Container>

      {/* Bottom Navigation - REMOVED */}
    </SpaceTheme>
  )
}

export default Dashboard
